import { useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import LegalHub from "@/components/LegalHub";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  WmtFormInput,
  WmtFormSubmitButton,
  WmtPhoneInput,
} from "@/components/ui/wmt-form-controls";
import { submitLead } from "@/lib/submitLead";
import MarketingConsent from "@/components/MarketingConsent";

const CASEBOOK_URL =
  "https://t.me/wmt_ai_iinsait_bot?start=dl-1787147677343&utm_source=wmt_ai";

const schema = z.object({
  name: z.string().trim().min(2, "Укажите имя").max(100, "Имя слишком длинное"),
  email: z.string().trim().email("Укажите корректный email").max(255),
  phone: z
    .string()
    .min(1, "Укажите телефон")
    .refine((value) => isValidPhoneNumber(value), {
      message: "Введите корректный номер телефона",
    }),
});

type FormState = z.infer<typeof schema>;

const initialForm: FormState = { name: "", email: "", phone: "" };

type CasebookDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const CasebookDialog = ({ open, onOpenChange }: CasebookDialogProps) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field: keyof FormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = schema.safeParse(form);

    if (!parsed.success) {
      const nextErrors: Partial<Record<keyof FormState, string>> = {};
      parsed.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormState;
        if (!nextErrors[field]) nextErrors[field] = error.message;
      });
      setErrors(nextErrors);
    } else {
      setErrors({});
    }

    if (!consent) setConsentError(true);
    if (!parsed.success || !consent) return;

    setSubmitting(true);
    const { name, phone, email } = parsed.data;
    try {
      await submitLead({ form_type: "casebook", name, phone, email, message: "Запрос полного кейсбука WMT AI", marketing_consent: marketingConsent });
    } catch {
      toast.error("Не удалось отправить данные. Попробуйте ещё раз.");
      return;
    } finally {
      setSubmitting(false);
    }

    window.ym?.(109037743, "reachGoal", "casebook_form_submit");
    setForm(initialForm);
    setConsent(false);
    setMarketingConsent(false);
    setSubmitted(true);
  };

  const handleOpenCasebook = () => {
    window.ym?.(109037743, "reachGoal", "casebook_telegram_click");
    window.location.assign(CASEBOOK_URL);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) setSubmitted(false);
        }}
      >
        <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-[742px] overflow-y-auto rounded-[32px] border-0 bg-white px-5 py-8 shadow-2xl sm:px-[35px] sm:py-12 [&>button]:hidden">
          {submitted ? (
            <div className="flex flex-col items-center py-8 text-center sm:py-10">
              <CheckCircle size={52} className="text-[#f04f23]" />
              <DialogTitle className="mt-6 font-heading text-[clamp(2.25rem,7vw,4rem)] font-normal leading-none tracking-[-0.025em] text-[#303030]">
                Спасибо
              </DialogTitle>
              <DialogDescription className="mt-5 max-w-[520px] text-base leading-7 text-[#666] sm:text-lg">
                Спасибо за заявку. Теперь вы можете забрать полный кейсбук WMT AI.
              </DialogDescription>
              <button
                type="button"
                onClick={handleOpenCasebook}
                className="mt-8 inline-flex h-14 items-center justify-center rounded-full bg-[#303030] px-10 font-heading text-base font-semibold text-white transition-colors hover:bg-[#222] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f04f23] focus-visible:ring-offset-2"
              >
                Забрать кейсбук
              </button>
            </div>
          ) : (
            <>
              <DialogHeader className="relative items-center space-y-0 text-center sm:text-center">
                <DialogTitle className="relative z-10 whitespace-normal font-heading text-[clamp(2.5rem,8vw,4.5rem)] font-normal leading-none tracking-[-0.025em] text-[#303030] sm:whitespace-nowrap">
                  Получить кейсбук
                </DialogTitle>
                <DialogDescription className="max-w-[560px] pt-6 text-base leading-7 text-[#666] sm:text-lg">
                  Заполните данные и получите доступ к подробному кейсбуку по услугам
                  компании
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} noValidate className="pt-8 sm:pt-12">
                <div className="grid gap-4 sm:grid-cols-2">
                  <WmtFormInput
                    id="casebook-name"
                    type="text"
                    autoComplete="name"
                    aria-label="ФИО"
                    placeholder="ФИО"
                    value={form.name}
                    onChange={(event) => updateField("name", event.target.value)}
                    error={errors.name}
                    className="rounded-2xl bg-[#fcfcfc]"
                  />
                  <WmtFormInput
                    id="casebook-email"
                    type="email"
                    autoComplete="email"
                    aria-label="Email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    error={errors.email}
                    className="rounded-2xl bg-[#fcfcfc]"
                  />
                </div>

                <div className="mt-4">
                  <WmtPhoneInput
                    id="casebook-phone"
                    value={form.phone}
                    onChange={(value) => updateField("phone", value)}
                    error={errors.phone}
                    className="rounded-2xl bg-[#fcfcfc]"
                  />
                </div>

                <div className="mt-5 flex items-start gap-3">
                  <input
                    id="casebook-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => {
                      setConsent(event.target.checked);
                      if (event.target.checked) setConsentError(false);
                    }}
                    className="mt-1 h-3.5 w-3.5 shrink-0 rounded border-[#666] accent-[#f04f23]"
                  />
                  <label htmlFor="casebook-consent" className="text-sm leading-5 text-[#666]/70">
                    Я даю согласие на обработку персональных данных на условиях{" "}
                    <button
                      type="button"
                      onClick={() => setLegalOpen(true)}
                      className="underline underline-offset-2 hover:text-[#f04f23]"
                    >
                      отдельного согласия
                    </button>.
                  </label>
                </div>
                {consentError && (
                  <p className="mt-1.5 text-xs text-destructive">
                    Необходимо согласиться с обработкой персональных данных
                  </p>
                )}
                <MarketingConsent id="casebook-marketing-consent" checked={marketingConsent} onChange={setMarketingConsent} className="mt-3 flex items-start gap-3" checkboxClassName="mt-1 h-3.5 w-3.5 shrink-0 rounded border-[#666] accent-[#f04f23]" labelClassName="text-left text-sm leading-5 text-[#666]/70" />

                <div className="mt-6 flex justify-center">
                  <WmtFormSubmitButton
                    submitting={submitting}
                    submittingLabel="Отправка…"
                    className="gap-3 px-12"
                  >
                    <span className="optical-center">Получить кейсбук</span>
                    <img
                      src="/images/telegram-circle-arrow.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-6 w-6"
                    />
                  </WmtFormSubmitButton>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <LegalHub open={legalOpen} onOpenChange={setLegalOpen} defaultSection="confidentiality" />
    </>
  );
};

export default CasebookDialog;
